/**
 * formatTimestamp — Round 6.5 Item 26; account fallback added Push 4-1d-d.
 *
 * Render a server-stored UTC ISO timestamp in the user's local timezone,
 * using their preferred IANA zone from sys__account_user.timezone. Replaces
 * scattered `new Date(iso).toLocaleString()` callsites that always defaulted
 * to the browser's runtime timezone (which is what a user in a borrowed
 * laptop / VPN sees, not what the user actually means by "their" time).
 *
 * Why a shared helper:
 *   - All server timestamps come back as UTC ISO strings from Postgres
 *     (`::text` cast on `timestamptz` columns). The renderer has to pick a
 *     zone; the SPA should pick the user's saved zone, not the runtime.
 *   - One place to control the date/time format so a future "use 24-hour
 *     time in the EU" or "always show year for >30d-old timestamps" rule
 *     lands once, not in 100 callsites.
 *
 * Resolution order (Push 4-1d-d, 2026-07-01):
 *   1. userTz — per-user override from sys__account_user.timezone
 *   2. accountTz — account default from sys__account.timezone (Migration 0143)
 *   3. Browser runtime — Intl.DateTimeFormat default
 *
 * Defaults:
 *   - 'medium' date + 'short' time (e.g. "Jun 25, 2026, 9:32 AM"). This is
 *     the same shape `.toLocaleString()` produces for most en-US locales,
 *     so the migration is visually low-friction.
 *   - When both userTz and accountTz are undefined or unparseable, falls back
 *     to the runtime default (preserves backward compatibility with the
 *     legacy `.toLocaleString()` behavior at callsites that don't have a
 *     session context handy).
 */

export function formatTimestamp(
  utcIso: string,
  userTz?: string,
  accountTz?: string,
): string {
  // Defensive — bad input returns a passthrough rather than throwing.
  const d = new Date(utcIso);
  if (isNaN(d.getTime())) return utcIso;

  const options: Intl.DateTimeFormatOptions = {
    dateStyle: 'medium',
    timeStyle: 'short',
  };

  // Push 4-1d-d — try user override first, then account default, then runtime.
  const candidates: string[] = [];
  if (userTz !== undefined && userTz !== '') candidates.push(userTz);
  if (accountTz !== undefined && accountTz !== '') candidates.push(accountTz);

  for (const tz of candidates) {
    try {
      return new Intl.DateTimeFormat(undefined, {
        ...options,
        timeZone: tz,
      }).format(d);
    } catch {
      // Fall through — tz wasn't a valid IANA name (should be rare given
      // server-side validation, but a stale cached CurrentUser might carry
      // a value a newer runtime rejects).
    }
  }

  return new Intl.DateTimeFormat(undefined, options).format(d);
}

/**
 * Convenience: returns the IANA zone list when the browser supports
 * Intl.supportedValuesOf('timeZone'), otherwise a curated fallback of ~30
 * commonly-used zones. The fallback list is alphabetical-by-display-name
 * and covers the major world regions Olytic customers operate in.
 *
 * Used by the Profile timezone picker.
 */
export function listTimezones(): string[] {
  // `supportedValuesOf` was added in Chrome 99, Firefox 99, Safari 15.4.
  // Most users will get the full ~600-zone list; the fallback only fires
  // on outdated runtimes.
  const intlWithSupported = Intl as unknown as {
    supportedValuesOf?: (key: 'timeZone') => string[];
  };
  if (typeof intlWithSupported.supportedValuesOf === 'function') {
    try {
      return intlWithSupported.supportedValuesOf('timeZone');
    } catch {
      /* fall through to static list */
    }
  }
  return FALLBACK_TZ_LIST;
}

/**
 * Hardcoded fallback list (~30 zones) when Intl.supportedValuesOf is
 * unavailable. Covers Americas / Europe / Africa / Asia / Australia /
 * Pacific. Kept in alphabetical order by IANA name.
 */
const FALLBACK_TZ_LIST: string[] = [
  'Africa/Cairo',
  'Africa/Johannesburg',
  'Africa/Lagos',
  'America/Anchorage',
  'America/Argentina/Buenos_Aires',
  'America/Chicago',
  'America/Denver',
  'America/Halifax',
  'America/Los_Angeles',
  'America/Mexico_City',
  'America/New_York',
  'America/Phoenix',
  'America/Sao_Paulo',
  'America/Toronto',
  'America/Vancouver',
  'Asia/Dubai',
  'Asia/Hong_Kong',
  'Asia/Jerusalem',
  'Asia/Kolkata',
  'Asia/Seoul',
  'Asia/Shanghai',
  'Asia/Singapore',
  'Asia/Tokyo',
  'Australia/Melbourne',
  'Australia/Sydney',
  'Europe/Berlin',
  'Europe/Dublin',
  'Europe/London',
  'Europe/Madrid',
  'Europe/Paris',
  'Europe/Rome',
  'Pacific/Auckland',
  'Pacific/Honolulu',
  'UTC',
];

/**
 * Best-effort guess of the browser's current timezone. Used as the initial
 * value when a user has no saved timezone (or as a "Reset to browser
 * default" affordance on the Profile picker).
 */
export function browserDefaultTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}
